/** angular */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
/** end angular */

/** models */
import { BydGenerateParams, BydModel } from '@sixense/byd-core-service';
import { DsMapCategory } from '../models/ds-map-category.class';
/** models */

/** services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
/** end services */

/** interfaces */
import { DsMapCategoryInterface } from '../interfaces/ds-map-category.interface';
import { DS_MAPS_BASE_URL } from '../constants/ds-maps-base-url.constant';
import { PageModel } from 'projects/byd-roazhon-service/src/byd-rhs-index';
import { DsMapCategoryQueryFilterModel } from '../models/ds-map-category-query-filter.model';
/** end interfaces */

@Injectable({
  providedIn: 'root',
})
export class DsMapCategoriesService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Get the map categories.
   *
   * @param projectId the project id
   * @returns Observable<BydModel<DsMapCategory>>
   */
  public getMapCategories(projectId: number): Observable<BydModel<DsMapCategory>> {
    this.path = `/projects/${projectId}/plancategories`;
    return this.getModels<DsMapCategory>();
  }

  /**
   * Get the map categories.
   *
   * @param projectId the project id
   * @param pageMapCategory - PageModel that contains pagination information.
   * @param filters - Map category filters object.
   * @returns Observable<BydModel<DsMapCategory>>
   */
  public getMapCategoriesV2(
    projectId: number,
    pageMapCategory: PageModel,
    filters?: DsMapCategoryQueryFilterModel
  ): Observable<BydModel<DsMapCategory>> {
    this.path = `${DS_MAPS_BASE_URL}${projectId}/mapcategories`;
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(
      pageMapCategory.elementsPerPage,
      pageMapCategory.pageIndex
    );
    const params = BydGenerateParams(filters);
    return this.getModels<DsMapCategory>({ headers, params });
  }

  /**
   * Add a map category.
   *
   * @param projectId The project id
   * @param categoryName Map category name
   * @returns SQL insert Object
   */
  public addMapCategory(projectId: number, categoryName: string) {
    this.path = `/projects/${projectId}/plancategories`;
    const body = { planCatName: categoryName };
    return this.post({ body });
  }

  /**
   * Rename a map category.
   *
   * @param projectId The project id
   * @param categoryId Map category id
   * @param newName New name for the map category
   * @returns Observable<{ name: string }>
   */
  public renameMapCategory(
    projectId: number,
    categoryId: number,
    newName: string
  ): Observable<{
    /**
     *
     */
    name: string;
  }> {
    this.path = `${DS_MAPS_BASE_URL}${projectId}/mapCategories/2D/${categoryId}`;
    return this.put<{
      /**
       *
       */
      name: string;
    }>({ body: { mapCategoryName: newName } });
  }

  /**
   * Delete the selected map category by id.
   *
   * @param projectId The project id
   * @param categoryId Map category id
   * @returns SQL delete Object
   */
  public deleteMapCategory(projectId: number, categoryId: number) {
    this.path = `/projects/${projectId}/plancategories/${categoryId}`;
    return this.delete();
  }

  /**
   * Returns list of map categories and whether they can be deleted.
   *
   * @param projectId The project id
   * @returns Observable<BydModel<DsMapCategory>>
   */
  public getDetailedCategories(projectId: number): Observable<BydModel<DsMapCategory>> {
    this.path = `/projects/${projectId}/planscountcategories`;
    return this.getModels<DsMapCategory>();
  }

  /**
   * Create DsMapCategory instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsMapCategory` instance.
   */
  public createNewInstance(jsonObject: DsMapCategoryInterface): DsMapCategory {
    return new DsMapCategory(jsonObject);
  }
}
